import {
  AddButtonWrapper,
  ForenameColumn,
  GuestCounter,
  GuestRow,
  InputsRow,
  OptionalIndicator,
  RemoveGuestButtonWrapper,
  SurnameColumn,
  TitleColumn,
} from '@common/components/BookingForms/GuestField/GuestField.styles';
import RemoveGuestButton from '@common/components/BookingForms/GuestField/RemoveGuestButton';
import { Button } from '@common/components/Button';
import { BlockInputField } from '@common/components/FormElements';
import { BlockSelectField } from '@common/components/FormElements/BlockSelectField';
import { useTranslations } from '@lang';
import { AddIcon } from '@root/styles/icons';
import { femaleTitlesOptions, maleTitlesOptions } from '@utils/guestTitles';
import { Controller, UseFieldArrayReturn, useFormContext, useWatch } from 'react-hook-form';
import { RoomRateFormValues } from '../RoomRateFormV2.schema';

type GuestFieldV2Props = {
  fieldArray: UseFieldArrayReturn<RoomRateFormValues, 'guests', 'id'>;
  minimumGuests: number;
  maximumGuests?: number;
  withLeadGuest?: boolean;
};

export const GuestFieldV2 = ({
  fieldArray,
  minimumGuests,
  maximumGuests,
  withLeadGuest = false,
}: GuestFieldV2Props) => {
  const { t } = useTranslations();
  const { control, setValue } = useFormContext<RoomRateFormValues>();
  const { fields, append, remove } = fieldArray;
  const guestValues = useWatch({ control, name: 'guests' }) ?? [];

  const titlesOptions = [...maleTitlesOptions, ...femaleTitlesOptions].map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const handleRemove = (index: number) => {
    remove(index);
    if (fields.length - 1 < minimumGuests) {
      append({ firstName: '', lastName: '', title: '' });
    }
  };

  const handleAdd = () => {
    if (!maximumGuests || fields.length < maximumGuests) {
      append({ title: '', firstName: '', lastName: '' });
    }
  };

  const canAddNextGuest = !maximumGuests || fields.length < maximumGuests;

  return (
    <>
      {fields.map((fieldEntry, index) => {
        const guestData = guestValues[index] ?? {};
        const isExistingBasketGuest = !!(guestData.externalSpaId || guestData.id);
        const isGenderDataMissing = !(guestData.title || guestData.gender);
        const isOptionalIndicatorVisible = index > 0 && withLeadGuest;
        const isFieldRequired = !isOptionalIndicatorVisible;

        return (
          <GuestRow key={fieldEntry.id}>
            <GuestCounter>
              {withLeadGuest && index === 0
                ? t('common.leadGuest')
                : t('spa.guestCounter', { counter: index + 1 })}
              {isOptionalIndicatorVisible && (
                <OptionalIndicator> {t('common.optional')}</OptionalIndicator>
              )}
            </GuestCounter>
            <InputsRow>
              <TitleColumn>
                <Controller
                  control={control}
                  name={`guests.${index}.title`}
                  render={({ field: { onChange, value } }) => (
                    <BlockSelectField
                      useNativeDropdownOnMobile={false}
                      labelWidth="50px"
                      options={titlesOptions}
                      value={titlesOptions.find((opt) => opt.value === value) ?? null}
                      label={t('common.title')}
                      isRequired={isFieldRequired}
                      isDisabled={isExistingBasketGuest && !isGenderDataMissing}
                      isSearchable={false}
                      id={`${index}-title`}
                      onChange={(selected: any) => {
                        const val = selected?.value ?? '';
                        onChange(val);
                        setValue(`guests.${index}.gender`, val === 'Mr' ? 'male' : 'female');
                      }}
                    />
                  )}
                />
              </TitleColumn>
              <ForenameColumn>
                <Controller
                  control={control}
                  name={`guests.${index}.firstName`}
                  render={({ field: { onChange, value } }) => (
                    <BlockInputField
                      isRequired={isFieldRequired}
                      label={t('common.firstName')}
                      placeholder={t('common.firstName')}
                      disabled={isExistingBasketGuest}
                      id={`${index}-fname`}
                      value={value ?? ''}
                      onChange={onChange}
                    />
                  )}
                />
              </ForenameColumn>
              <SurnameColumn>
                <Controller
                  control={control}
                  name={`guests.${index}.lastName`}
                  render={({ field: { onChange, value } }) => (
                    <BlockInputField
                      isRequired={isFieldRequired}
                      label={t('common.lastName')}
                      placeholder={t('common.lastName')}
                      disabled={isExistingBasketGuest}
                      id={`${index}-lname`}
                      value={value ?? ''}
                      onChange={onChange}
                    />
                  )}
                />
              </SurnameColumn>
              <RemoveGuestButtonWrapper>
                <RemoveGuestButton
                  isVisible={isExistingBasketGuest || index >= minimumGuests}
                  onClick={() => handleRemove(index)}
                />
              </RemoveGuestButtonWrapper>
            </InputsRow>
          </GuestRow>
        );
      })}
      <AddButtonWrapper>
        {canAddNextGuest && (
          <Button
            type="button"
            isSmall
            onClick={handleAdd}
            icon={<AddIcon width="16" height="16" />}
          >
            {t('common.addMoreGuests')}
          </Button>
        )}
      </AddButtonWrapper>
    </>
  );
};
